import React, { useState, useEffect } from 'react';
import { Container, Card } from 'react-bootstrap';
import { createColSectionInstance } from '../../Models/ColSection';
import './ColSection.css';
import CTAPrimaryButton from '../CTAButton/CTAPrimaryButton/CTAPrimaryButton';
import CTASecondaryButton from '../CTAButton/CTASecondaryButton/CTASecondaryButton';
import { useNavigate } from 'react-router-dom';
import { refreshAosLite } from '../../Services/Animations/aosLite';

const ColSection = (props) => {
    const [colSection, setColSection] = useState(createColSectionInstance());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Classification helpers (same approach as MediaCarousel)
    const isVideoFile = (filePath) => /\.(mp4|mov|webm|avi|mkv|m4v|ogg|wmv)$/i.test(filePath || '');
    const isImageFile = (filePath) => /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|tiff|tif)$/i.test(filePath || '');

    const getVideoEmbedUrl = (url) => {
        if (!url) return '';
        
        const lowerUrl = url.toLowerCase();
        
        // YouTube
        if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
            const videoId = lowerUrl.includes('youtube.com') 
                ? url.split('v=')[1]?.split('&')[0]
                : url.split('youtu.be/')[1]?.split('?')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        }
        
        // Vimeo
        if (lowerUrl.includes('vimeo.com')) {
            const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
            return `https://player.vimeo.com/video/${videoId}`;
        }
        
        // Dailymotion
        if (lowerUrl.includes('dailymotion.com')) {
            const videoId = url.split('dailymotion.com/video/')[1]?.split('?')[0];
            return `https://www.dailymotion.com/embed/video/${videoId}`;
        }
        
        // Direct video file
        return url;
    };

    useEffect(() => {
        const fetchDataAndApplyStyles = async () => {
            try {
                setIsLoading(true);
                await colSection.load(props.id);
                colSection.renderStyles();
                
                setIsLoading(false);
                try { setTimeout(() => refreshAosLite(), 50); } catch (e) {}
            } catch (err) {
                console.error('Failed to load column section data:', err);
                setError('Failed to load column section data');
                setIsLoading(false);
            }
        };

        fetchDataAndApplyStyles();
    }, [colSection, props.id]);

    if (isLoading) {
        return <div className="loading-placeholder">Loading column section...</div>;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    const mediaUrl = colSection.getImageUrl();
    let mediaType = undefined;
    if (mediaUrl) {
        if (isVideoFile(mediaUrl)) mediaType = 'video';
        else if (isImageFile(mediaUrl)) mediaType = 'image';
        else mediaType = 'embed';
    }
    const leftAnim = colSection.getLeftAnimation();
    const rightAnim = colSection.getRightAnimation();
    const leftShould = leftAnim && leftAnim.animationType && leftAnim.animationType !== 'none';
    const rightShould = rightAnim && rightAnim.animationType && rightAnim.animationType !== 'none';

    return (
        <Container fluid className={`ColSection ColSection-${colSection.getId()}`}>
            <div className="container">
                <div className={`row align-items-center ${colSection.getDirection() === 'reversed' ? 'flex-row-reverse' : ''}`}>
                    {/* Left Column - Media (Image or Video) */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                        <div 
                            className={`ColSectionImage ColSectionImage-${colSection.getId()} d-flex justify-content-center align-items-center p-2`}
                            {...(leftShould ? {
                                'data-aos': leftAnim.animationType,
                                'data-aos-duration': Number(leftAnim.animationDuration),
                                'data-aos-delay': Number(leftAnim.animationDelay),
                            } : {})}
                        >
                            {mediaType === 'embed' ? (
                                <iframe
                                    src={mediaUrl}
                                    className="video-preview"
                                    style={{ border: 'none' }}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    title="embedded-media"
                                />
                            ) : mediaType === 'video' ? (
                                <video
                                    src={mediaUrl}
                                    className="video-preview"
                                    muted
                                    preload="metadata"
                                    controls={false}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        const fallback = e.target.nextSibling;
                                        if (fallback) fallback.style.display = 'flex';
                                    }}
                                />
                            ) : (
                                <Card style={{ width: '100%', maxWidth: '500px', border: 'none' , backgroundColor: 'transparent'}}>
                                    <Card.Img 
                                        src={mediaUrl} 
                                        style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/placeholder-image.jpg';
                                        }}
                                    />
                                </Card>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Content */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                        <div 
                            className={`ColSectionContent ColSectionContent-${colSection.getId()}`}
                            {...(rightShould ? {
                                'data-aos': rightAnim.animationType,
                                'data-aos-duration': Number(rightAnim.animationDuration),
                                'data-aos-delay': Number(rightAnim.animationDelay),
                            } : {})}
                        >
                            <h2 className={`ColSectionTitle ColSectionTitle-${colSection.getId()}`}>
                                {colSection.getTitle()}
                            </h2>
                            <p className={`ColSectionDescription ColSectionDescription-${colSection.getId()}`}>
                                {colSection.getDescription()}
                            </p>
                            
                            {/* Buttons */}
                            <div className={`ColSectionButtons ColSectionButtons-${colSection.getId()} ${colSection.getButtons().length > 1 ? 'd-flex' : ''}`}>
                                {colSection.getButtons().map((btn) => (
                                    btn.button.buttonType === 'primary' ? 
                                    <CTAPrimaryButton
                                        key={btn.id}
                                        text={btn.button.text}
                                        type={btn.button.type}
                                        link={btn.button.link}
                                        className={btn.button.className}
                                        style={btn.button.style}
                                        disabled={false}
                                        onClick={() => {
                                            navigate(btn.button.link);
                                        }}
                                    />
                                    :
                                    <CTASecondaryButton
                                        key={btn.id}
                                        text={btn.button.text}
                                        type={btn.button.type}
                                        link={btn.button.link}
                                        className={btn.button.className}
                                        style={btn.button.style}
                                        disabled={false}
                                        onClick={() => {
                                            navigate(btn.button.link);
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    );
};

export default ColSection;
